/* eslint-disable no-await-in-loop */
/* eslint-disable no-plusplus */
/* eslint-disable no-restricted-syntax */
import Swal from 'sweetalert2';

import { TranslateFn } from 'hooks/useTranslate';
import { Society, User } from 'types';

import {
  sendEnvelopeReminder,
  getEnvelopesStatusById,
  resendVoidedEnvelope,
} from 'modules/_shared/redux/envelopeActions';
import { confirmationModalCreator } from 'modules/_shared/components/Modals/utils/confirmationModalCreator';
import { BeneficiaryWithEnvelopeId } from 'modules/beneficiaries/hooks/useGetBeneficiariesPendingEnvelopes';

import { hasShowDetails } from 'utils/showDetails';
import { format } from 'date-fns';
import { EnvelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';

type ResendInvitationProps = {
  t: TranslateFn;
  actualSociety: Society;
  user: User;
  filteredBeneficiaries: BeneficiaryWithEnvelopeId[];
  setIsResendingInvitations: (loading: boolean) => void; // eslint-disable-line no-unused-vars
  setResendingBeneficiaries: (beneficiaries: Set<string>) => void; // eslint-disable-line no-unused-vars
  updateBeneficiaryStatus: (
    beneficiaryId: string, // eslint-disable-line no-unused-vars
    invitationStatus: string, // eslint-disable-line no-unused-vars
    envelopeStatus?: string // eslint-disable-line no-unused-vars
  ) => void;
};

const resendInvitation = async ({
  t,
  actualSociety,
  filteredBeneficiaries,
  setIsResendingInvitations,
  setResendingBeneficiaries,
  updateBeneficiaryStatus,
}: ResendInvitationProps) => {
  const validBeneficiaries = filteredBeneficiaries.filter(
    (beneficiary) => beneficiary.envelopeId
  );

  Swal.fire({
    html: confirmationModalCreator({
      title: t('ResendInvitation'),
      footer: t('ResendInvitationPendingInvitationsFootMessage'),
      message: t('ResendInvitationPendingInvitations', {
        beneficiariesCount: validBeneficiaries.length,
        societyName: actualSociety.name,
      }),
    }),
    confirmButtonText: t('ResendNow'),
    confirmButtonColor: '#6576ff',
    showCloseButton: true,
  }).then(async (result) => {
    if (result.isConfirmed) {
      setIsResendingInvitations(true);
      const resendLog = [];
      const resendResult = { SUCCESS: 0, ERROR: 0 };
      const beneficiaryIds = new Set<string>();

      try {
        for (const beneficiary of validBeneficiaries) {
          try {
            const { envelopeId, envelopeSignatureId, envelopeStatus, _id } =
              beneficiary;

            if (!_id) {
              resendResult.ERROR++;
            } else {
              // Add beneficiary to resending set
              beneficiaryIds.add(_id);
              setResendingBeneficiaries(new Set(beneficiaryIds));

              if (envelopeStatus === EnvelopeStatus.VOIDED) {
                await resendVoidedEnvelope(envelopeId, envelopeSignatureId);
              } else {
                await sendEnvelopeReminder(envelopeId, envelopeSignatureId);
              }

              // Update invitation status to RESENT
              updateBeneficiaryStatus(_id, 'RESENT');

              // Fetch updated envelope status
              try {
                const envelopeStatuses = await getEnvelopesStatusById([
                  envelopeSignatureId,
                ]);
                if (
                  envelopeStatuses &&
                  Array.isArray(envelopeStatuses) &&
                  envelopeStatuses.length > 0
                ) {
                  const envelopeStatus = envelopeStatuses[0];
                  if (envelopeStatus?.envelopeStatus?.envelopeStatus) {
                    updateBeneficiaryStatus(
                      _id,
                      'RESENT',
                      envelopeStatus.envelopeStatus.envelopeStatus
                    );
                  }
                }
              } catch (statusError) {
                console.warn('Failed to fetch envelope status:', statusError);
              }

              await new Promise((resolve) => setTimeout(resolve, 500));

              if (hasShowDetails()) {
                resendLog.push({
                  _id: beneficiary._id,
                  name: beneficiary.name,
                  email: beneficiary.email,
                  envelopeId: beneficiary.envelopeId,
                  envelopeSignatureId: beneficiary.envelopeSignatureId,
                  planName: beneficiary.planName,
                  planStartDate: format(
                    new Date(beneficiary.planStartDate),
                    'dd/MM/yyyy'
                  ),
                  resendDate: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
                  status: 'SUCCESS',
                });
              }
              resendResult.SUCCESS++;
            }
          } catch (error) {
            resendResult.ERROR++;
            if (hasShowDetails()) {
              resendLog.push({
                _id: beneficiary._id,
                name: beneficiary.name,
                email: beneficiary.email,
                envelopeId: beneficiary.envelopeId,
                envelopeSignatureId: beneficiary.envelopeSignatureId,
                planName: beneficiary.planName,
                planStartDate: format(
                  new Date(beneficiary.planStartDate),
                  'dd/MM/yyyy'
                ),
                resendDate: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
                status: 'ERROR',
              });
            }
            console.error(
              'Error resending invitation for beneficiary:',
              beneficiary._id,
              error
            );
          } finally {
            // Remove beneficiary from resending set
            if (beneficiary._id) {
              beneficiaryIds.delete(beneficiary._id);
              setResendingBeneficiaries(new Set(beneficiaryIds));
            }
          }
        }
      } finally {
        setIsResendingInvitations(false);
        setResendingBeneficiaries(new Set());
      }

      Swal.fire({
        html: confirmationModalCreator({
          title: t('ResendInvitationResults'),
          message: t('ResendInvitationResultsMessage', {
            successCount: resendResult.SUCCESS,
            errorCount: resendResult.ERROR,
          }),
        }),
        confirmButtonText: t('OK'),
        confirmButtonColor: '#6576ff',
        showCloseButton: true,
      });
    }
  });
};

export default resendInvitation;
